import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  LexicalTypeaheadMenuPlugin,
  MenuOption,
} from '@lexical/react/LexicalTypeaheadMenuPlugin';
import {
  $getNodeByKey,
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_CRITICAL,
  TextNode,
} from 'lexical';
import * as React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { ProjectMetadata, ProjectMetadataMap } from 'const/ProjectMetadata';
import { documentMetadata } from 'containers/RootDocument/selectors';
import { $fixTextNodeSelection } from '../../helpers/fixTextNodeSelection';
import { ProjectMetadataTokenNode, $createProjectMetadataTokenNode } from '../../nodes/ProjectMetadataTokenNode';
import { useEditModeContext } from '../EditModeContextPlugin';
import { registerCommands } from './commands';
import { lexicalTypeaheadMenuTriggerFn } from './typeahead/lexicalTypeaheadMenuTriggerFn';
import { menuRenderFn } from './typeahead/menuRenderFn';
import { useTypeaheadFloating } from './typeahead/useTypeaheadFloating';
import { $textNodeTransform } from './utils';

const SUGGESTION_COUNT = 10;

export const ProjectMetadataPlugin = (): JSX.Element | null => {
  const [editor] = useLexicalComposerContext();
  const editMode = useEditModeContext();
  const metadata = useSelector(documentMetadata);
  const isMounted = useRef(false);
  const [options, setOptions] = useState<MenuOption[]>([]);
  const floating = useTypeaheadFloating();

  const nodesCache = useMemo<Set<string>>(
    () => new Set(),
    [editor], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const $updateProjectMetadataTokenNodes = useCallback((key: string): void => {
    const node = $getNodeByKey<ProjectMetadataTokenNode>(key);
    if (!node) {
      return;
    }

    if (editMode) {
      node.switchToEditMode();
    } else {
      node.switchToViewMode();
    }

    $fixTextNodeSelection(node);
  }, [editMode]);

  const onQueryChange = useCallback((queryString: string | null) => {
    const metadataKeys = Object.keys(ProjectMetadataMap);

    let filteredKeys: string[];

    if (!queryString) {
      filteredKeys = metadataKeys;
    } else {
      const lowerQuery = queryString.toLowerCase();
      filteredKeys = metadataKeys.filter((key) => {
        const param = ProjectMetadataMap[key] as ProjectMetadata;

        return param?.title.toLowerCase().includes(lowerQuery);
      });
    }

    const finalOptions = filteredKeys
      .slice(0, SUGGESTION_COUNT)
      .map(key => new MenuOption(key));

    setOptions(finalOptions);

    return finalOptions;
  }, []);

  const onSelectOption = useCallback(
    (
      selectedOption: MenuOption,
      nodeToRemove: TextNode | null,
      closeMenu: () => void,
    ) => {
      editor.update(() => {
        const selection = $getSelection();

        if (!$isRangeSelection(selection) || selectedOption === null) {
          return;
        }

        const textTokenNode = $createProjectMetadataTokenNode(selectedOption.key, metadata);

        if (!textTokenNode) {
          return;
        }

        if (nodeToRemove) {
          nodeToRemove.remove();
          textTokenNode.setStyle(nodeToRemove.getStyle());
        }

        selection.insertNodes([textTokenNode]);

        closeMenu();
      });
    },
    [editor, metadata],
  );

  useEffect(() => {
    registerCommands(editor, metadata);
  }, [editor, metadata]);

  useEffect(() => {
    isMounted.current = true;

    return editor.registerNodeTransform(TextNode, $textNodeTransform(metadata));
  }, [metadata, editor]);

  useEffect(() => {
    if (!isMounted.current) {
      return;
    }
    editor.update(() => {
      nodesCache.forEach($updateProjectMetadataTokenNodes);
    });
  }, [metadata, editMode, editor, nodesCache, $updateProjectMetadataTokenNodes]);

  useEffect(() => {
    isMounted.current = true;

    return editor.registerMutationListener(
      ProjectMetadataTokenNode,
      (mutatedNodes) => {
        for (const [key, mutation] of mutatedNodes) {
          if (mutation === 'created') {
            nodesCache.add(key);
            editor.update(() => $updateProjectMetadataTokenNodes(key));
          } else if (mutation === 'destroyed') {
            nodesCache.delete(key);
          }
        }
      },
      { skipInitialization: false },
    );
  }, [editor, nodesCache, $updateProjectMetadataTokenNodes]);

  return editMode ? (
    <LexicalTypeaheadMenuPlugin
      onQueryChange={onQueryChange}
      onSelectOption={onSelectOption}
      triggerFn={lexicalTypeaheadMenuTriggerFn}
      options={options}
      commandPriority={COMMAND_PRIORITY_CRITICAL}
      menuRenderFn={menuRenderFn(options, floating)}
    />
  ) : null;
};
